import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  ConfirmDialog,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@mlm/ui';
import {
  BREATHWORK_LIMITS,
  breathPatternLabel,
  breathSessionSeconds,
  COACHING_BREATHWORK_OUTCOME_LABELS,
  COACHING_BREATHWORK_OUTCOMES,
  COACHING_FREQUENCY_CATEGORIES,
  COACHING_FREQUENCY_CATEGORY_LABELS,
  COACHING_TIER_LABELS,
  COACHING_TIERS,
  type CoachingBreathworkOutcome,
  type CoachingFrequencyCategory,
} from '@mlm/shared';
import { hasRole, useHasPerm } from '../lib/auth-store';
import {
  createCoachingContent,
  deleteCoachingContent,
  getCoachingBreathworkCatalog,
  listCoachingContent,
  patchCoachingContent,
  uploadCoachingAudio,
  type BreathworkCatalogEntry,
  type BreathworkTiming,
  type CoachingContentRow,
  type CoachingTier,
} from '../lib/api';

/**
 * Admin → Coaching → Content: the two Phase 4 libraries.
 *
 * Breathwork mirrors the reading-copy editor: the built-in patterns are listed
 * from the code that serves them, and editing one saves an override keyed to it
 * (hiding one is an inactive override; restoring deletes it). Frequencies are
 * uploaded files; removing one queues the file for the storage sweep.
 */

const TIMING_FIELDS: Array<{
  key: keyof BreathworkTiming;
  label: string;
  min: number;
  max: number;
}> = [
  { key: 'inhale', label: 'Breathe in (s)', min: 1, max: BREATHWORK_LIMITS.phaseMaxSeconds },
  { key: 'holdIn', label: 'Hold (s)', min: 0, max: BREATHWORK_LIMITS.phaseMaxSeconds },
  { key: 'exhale', label: 'Breathe out (s)', min: 1, max: BREATHWORK_LIMITS.phaseMaxSeconds },
  { key: 'holdOut', label: 'Hold after (s)', min: 0, max: BREATHWORK_LIMITS.phaseMaxSeconds },
  { key: 'cycles', label: 'Rounds', min: 1, max: BREATHWORK_LIMITS.cyclesMax },
];

const clock = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;

const audienceLabel = (level: CoachingTier) =>
  level === 'free' ? 'Everyone' : `${COACHING_TIER_LABELS[level]} and above`;

function AudienceSelect({
  value,
  onChange,
}: {
  value: CoachingTier;
  onChange: (v: CoachingTier) => void;
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">Who sees it</label>
      <Select value={value} onValueChange={(v) => onChange(v as CoachingTier)}>
        <SelectTrigger className="h-8 w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {COACHING_TIERS.map((t) => (
            <SelectItem key={t} value={t}>
              {audienceLabel(t)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ── Breathwork ─────────────────────────────────────────────────────────────

type BreathForm = {
  /** A built-in's key (an override), or null for the tenant's own pattern. */
  key: string | null;
  /** The row being edited, when one exists. */
  id: string | null;
  title: string;
  body: string;
  outcome: CoachingBreathworkOutcome;
  timing: Record<keyof BreathworkTiming, string>;
  premiumLevel: CoachingTier;
  active: boolean;
};

const numberOr = (v: unknown, fallback: number) =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;

function timingOfRow(row: CoachingContentRow, fallback?: BreathworkCatalogEntry): BreathworkTiming {
  return {
    inhale: numberOr(row.meta.inhale, fallback?.pattern.inhale ?? 4),
    holdIn: numberOr(row.meta.holdIn, fallback?.pattern.holdIn ?? 0),
    exhale: numberOr(row.meta.exhale, fallback?.pattern.exhale ?? 4),
    holdOut: numberOr(row.meta.holdOut, fallback?.pattern.holdOut ?? 0),
    cycles: numberOr(row.meta.cycles, fallback?.cycles ?? 6),
  };
}

const asFormTiming = (t: BreathworkTiming): BreathForm['timing'] => ({
  inhale: String(t.inhale),
  holdIn: String(t.holdIn),
  exhale: String(t.exhale),
  holdOut: String(t.holdOut),
  cycles: String(t.cycles),
});

const asOutcome = (v: string | null | undefined, fallback: CoachingBreathworkOutcome) =>
  (COACHING_BREATHWORK_OUTCOMES as readonly string[]).includes(v ?? '')
    ? (v as CoachingBreathworkOutcome)
    : fallback;

function parseTiming(t: BreathForm['timing']): BreathworkTiming | null {
  const out = {} as BreathworkTiming;
  for (const f of TIMING_FIELDS) {
    const n = Number(t[f.key]);
    if (!Number.isInteger(n) || n < f.min || n > f.max) return null;
    out[f.key] = n;
  }
  return out;
}

export function BreathworkEditor() {
  const qc = useQueryClient();
  const canWrite = useHasPerm('coaching.content.write') || hasRole('super_admin');
  const q = useQuery({ queryKey: ['coaching-breathwork'], queryFn: getCoachingBreathworkCatalog });
  const [form, setForm] = useState<BreathForm | null>(null);
  const [removing, setRemoving] = useState<CoachingContentRow | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['coaching-breathwork'] });

  const timing = form ? parseTiming(form.timing) : null;
  const valid = !!form && !!timing && form.title.trim().length >= 3 && form.body.trim().length >= 3;

  const save = useMutation({
    meta: { successMessage: 'Pattern saved.' },
    mutationFn: async () => {
      if (!form || !timing) return;
      const fields = {
        title: form.title.trim(),
        body: form.body.trim(),
        premiumLevel: form.premiumLevel,
      };
      if (form.key !== null || form.id === null) {
        // A built-in override is an upsert on its key; a new pattern is an insert.
        await createCoachingContent({
          kind: 'breathwork',
          ...(form.key ? { key: form.key } : {}),
          ...fields,
          outcome: form.outcome,
          active: form.active,
          ...timing,
        });
        return;
      }
      await patchCoachingContent(form.id, {
        ...fields,
        breathwork: { outcome: form.outcome, ...timing },
      });
    },
    onSuccess: () => {
      setForm(null);
      invalidate();
    },
  });
  const setActive = useMutation({
    mutationFn: async (args: {
      entry?: BreathworkCatalogEntry;
      row?: CoachingContentRow;
      active: boolean;
    }) => {
      const row = args.row ?? args.entry?.override ?? null;
      if (row) {
        await patchCoachingContent(row.id, { active: args.active });
        return;
      }
      // Hiding a built-in nobody has edited yet: save an inactive copy of it.
      const b = args.entry!;
      await createCoachingContent({
        kind: 'breathwork',
        key: b.key,
        title: b.title,
        body: b.description,
        outcome: b.outcome,
        premiumLevel: 'free',
        active: args.active,
        ...b.pattern,
        cycles: b.cycles,
      });
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    meta: { successMessage: 'Removed.' },
    mutationFn: (id: string) => deleteCoachingContent(id),
    onSuccess: invalidate,
  });

  const editBuiltIn = (b: BreathworkCatalogEntry) => {
    const o = b.override;
    setForm({
      key: b.key,
      id: o?.id ?? null,
      title: o?.title ?? b.title,
      body: o?.body ?? b.description,
      outcome: asOutcome(o?.category, b.outcome as CoachingBreathworkOutcome),
      timing: asFormTiming(o ? timingOfRow(o, b) : { ...b.pattern, cycles: b.cycles }),
      premiumLevel: o?.premiumLevel ?? 'free',
      active: o?.active ?? true,
    });
  };
  const editCustom = (row: CoachingContentRow) =>
    setForm({
      key: null,
      id: row.id,
      title: row.title ?? '',
      body: row.body,
      outcome: asOutcome(row.category, 'calm'),
      timing: asFormTiming(timingOfRow(row)),
      premiumLevel: row.premiumLevel,
      active: row.active,
    });

  const describe = (t: BreathworkTiming) =>
    `${breathPatternLabel(t)} · ${t.cycles} ${t.cycles === 1 ? 'round' : 'rounds'} · ${clock(
      breathSessionSeconds(t, t.cycles),
    )}`;

  return (
    <div className="space-y-4">
      {canWrite && !form && (
        <Button
          size="sm"
          onClick={() =>
            setForm({
              key: null,
              id: null,
              title: '',
              body: '',
              outcome: 'calm',
              timing: asFormTiming({ inhale: 4, holdIn: 0, exhale: 6, holdOut: 0, cycles: 10 }),
              premiumLevel: 'free',
              active: true,
            })
          }
        >
          Add a pattern
        </Button>
      )}

      {form && (
        <div className="space-y-3 rounded-md border bg-muted/20 p-3">
          <p className="text-sm font-medium">
            {form.key ? 'Edit a built-in pattern' : form.id ? 'Edit your pattern' : 'New pattern'}
          </p>
          <Input
            className="h-8"
            placeholder="Title, e.g. Calm before a call"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <Textarea
            rows={2}
            placeholder="What this breathing practice is for"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
          />
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Helps members feel</label>
              <Select
                value={form.outcome}
                onValueChange={(v) => setForm({ ...form, outcome: v as CoachingBreathworkOutcome })}
              >
                <SelectTrigger className="h-8 w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COACHING_BREATHWORK_OUTCOMES.map((o) => (
                    <SelectItem key={o} value={o}>
                      {COACHING_BREATHWORK_OUTCOME_LABELS[o]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {TIMING_FIELDS.map((f) => (
              <div key={f.key} className="space-y-1">
                <label className="text-xs text-muted-foreground">{f.label}</label>
                <Input
                  type="number"
                  min={f.min}
                  max={f.max}
                  className="h-8 w-24"
                  value={form.timing[f.key]}
                  onChange={(e) =>
                    setForm({ ...form, timing: { ...form.timing, [f.key]: e.target.value } })
                  }
                />
              </div>
            ))}
            <AudienceSelect
              value={form.premiumLevel}
              onChange={(premiumLevel) => setForm({ ...form, premiumLevel })}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {timing
              ? describe(timing)
              : `Breathe in and out need 1 to ${BREATHWORK_LIMITS.phaseMaxSeconds} seconds, holds 0 to ${BREATHWORK_LIMITS.phaseMaxSeconds}, and a session 1 to ${BREATHWORK_LIMITS.cyclesMax} rounds.`}
          </p>
          <div className="flex gap-2">
            <Button size="sm" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => setForm(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {q.data && (
        <>
          <h3 className="text-sm font-medium">Built-in patterns</h3>
          <div className="divide-y rounded-md border">
            {q.data.builtIns.map((b) => {
              const o = b.override;
              const t = o ? timingOfRow(o, b) : { ...b.pattern, cycles: b.cycles };
              const hidden = o !== null && !o.active;
              return (
                <div
                  key={b.key}
                  className="flex flex-wrap items-start justify-between gap-3 p-3 text-sm"
                >
                  <div className="min-w-0 space-y-1">
                    <p className={hidden ? 'text-muted-foreground line-through' : ''}>
                      {o?.title ?? b.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {
                        COACHING_BREATHWORK_OUTCOME_LABELS[
                          asOutcome(o?.category, b.outcome as CoachingBreathworkOutcome)
                        ]
                      }{' '}
                      · {describe(t)} · {audienceLabel(o?.premiumLevel ?? 'free')}
                    </p>
                    <div className="flex gap-1">
                      {o && <Badge variant="secondary">Edited</Badge>}
                      {hidden && <Badge variant="secondary">Hidden</Badge>}
                    </div>
                  </div>
                  {canWrite && (
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => editBuiltIn(b)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={setActive.isPending}
                        onClick={() => setActive.mutate({ entry: b, active: hidden })}
                      >
                        {hidden ? 'Show' : 'Hide'}
                      </Button>
                      {o && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(o.id)}
                        >
                          Restore original
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <h3 className="text-sm font-medium">Your patterns</h3>
          {q.data.custom.length === 0 ? (
            <p className="text-sm text-muted-foreground">None added yet.</p>
          ) : (
            <div className="divide-y rounded-md border">
              {q.data.custom.map((row) => {
                const t = timingOfRow(row);
                return (
                  <div
                    key={row.id}
                    className="flex flex-wrap items-start justify-between gap-3 p-3 text-sm"
                  >
                    <div className="min-w-0 space-y-1">
                      <p className={row.active ? '' : 'text-muted-foreground line-through'}>
                        {row.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {COACHING_BREATHWORK_OUTCOME_LABELS[asOutcome(row.category, 'calm')]} ·{' '}
                        {describe(t)} · {audienceLabel(row.premiumLevel)}
                      </p>
                    </div>
                    {canWrite && (
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => editCustom(row)}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={setActive.isPending}
                          onClick={() => setActive.mutate({ row, active: !row.active })}
                        >
                          {row.active ? 'Hide' : 'Show'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setRemoving(row)}>
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title="Remove this pattern?"
        description="Members stop seeing it. The built-in patterns are unaffected."
        confirmLabel="Remove"
        confirmVariant="destructive"
        loading={remove.isPending}
        onConfirm={async () => {
          await remove.mutateAsync(removing!.id);
          setRemoving(null);
        }}
      />
    </div>
  );
}

// ── Frequencies ────────────────────────────────────────────────────────────

const MAX_AUDIO_MB = 40;

const asCategory = (v: string | null | undefined): CoachingFrequencyCategory =>
  (COACHING_FREQUENCY_CATEGORIES as readonly string[]).includes(v ?? '')
    ? (v as CoachingFrequencyCategory)
    : 'meditation';

function CategorySelect({
  value,
  onChange,
}: {
  value: CoachingFrequencyCategory;
  onChange: (v: CoachingFrequencyCategory) => void;
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">Listening intention</label>
      <Select value={value} onValueChange={(v) => onChange(v as CoachingFrequencyCategory)}>
        <SelectTrigger className="h-8 w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {COACHING_FREQUENCY_CATEGORIES.map((c) => (
            <SelectItem key={c} value={c}>
              {COACHING_FREQUENCY_CATEGORY_LABELS[c]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Read a file's duration in the browser, so the member sees a length without the server decoding audio. */
function readDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = 'metadata';
    const done = (value: number | null) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    audio.onloadedmetadata = () =>
      done(
        Number.isFinite(audio.duration) && audio.duration > 0 ? Math.round(audio.duration) : null,
      );
    audio.onerror = () => done(null);
    audio.src = url;
  });
}

export function AudioEditor() {
  const qc = useQueryClient();
  const canWrite = useHasPerm('coaching.content.write') || hasRole('super_admin');
  const q = useQuery({
    queryKey: ['coaching-content', 'audio'],
    queryFn: () => listCoachingContent('audio'),
  });
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState<CoachingFrequencyCategory>('relaxation');
  const [label, setLabel] = useState('');
  const [premiumLevel, setPremiumLevel] = useState<CoachingTier>('free');
  const [editing, setEditing] = useState<{
    id: string;
    title: string;
    category: CoachingFrequencyCategory;
    label: string;
    premiumLevel: CoachingTier;
  } | null>(null);
  const [removing, setRemoving] = useState<CoachingContentRow | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['coaching-content', 'audio'] });

  const tooBig = !!file && file.size > MAX_AUDIO_MB * 1024 * 1024;
  const pick = async (next: File | null) => {
    setFile(next);
    setDuration(null);
    if (!next) return;
    if (!title.trim()) {
      setTitle(
        next.name
          .replace(/\.[^.]+$/, '')
          .replace(/[-_]+/g, ' ')
          .trim()
          .slice(0, 80),
      );
    }
    setDuration(await readDuration(next));
  };

  const upload = useMutation({
    meta: { successMessage: 'Track uploaded.' },
    mutationFn: () => {
      const form = new FormData();
      form.append('title', title.trim());
      form.append('body', body.trim());
      form.append('category', category);
      if (label.trim()) form.append('frequencyLabel', label.trim());
      if (duration) form.append('durationSeconds', String(duration));
      form.append('premiumLevel', premiumLevel);
      form.append('file', file!);
      return uploadCoachingAudio(form);
    },
    onSuccess: () => {
      setFile(null);
      setDuration(null);
      setTitle('');
      setBody('');
      setLabel('');
      if (fileInput.current) fileInput.current.value = '';
      invalidate();
    },
  });
  const saveEdit = useMutation({
    meta: { successMessage: 'Track saved.' },
    mutationFn: () =>
      patchCoachingContent(editing!.id, {
        title: editing!.title.trim(),
        premiumLevel: editing!.premiumLevel,
        audio: { category: editing!.category, frequencyLabel: editing!.label.trim() || null },
      }),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });
  const toggle = useMutation({
    mutationFn: (row: CoachingContentRow) => patchCoachingContent(row.id, { active: !row.active }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    meta: { successMessage: 'Track removed.' },
    mutationFn: (id: string) => deleteCoachingContent(id),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-4">
      {canWrite && (
        <div className="space-y-2 rounded-md border bg-muted/20 p-3">
          <input
            ref={fileInput}
            type="file"
            accept=".mp3,.m4a,audio/mpeg,audio/mp4"
            className="block text-sm"
            onChange={(e) => void pick(e.target.files?.[0] ?? null)}
          />
          {tooBig && (
            <p className="text-xs text-destructive">
              That file is larger than {MAX_AUDIO_MB} MB. Export it at a lower bitrate and try
              again.
            </p>
          )}
          <Input
            className="h-8"
            placeholder="Title, e.g. Abundance focus"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Textarea
            rows={2}
            placeholder="A reflective sound session for intention-setting and focused visualization."
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="flex flex-wrap items-end gap-3">
            <CategorySelect value={category} onChange={setCategory} />
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Frequency label (optional)</label>
              <Input
                className="h-8 w-40"
                placeholder="528 Hz"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <AudienceSelect value={premiumLevel} onChange={setPremiumLevel} />
            <Button
              size="sm"
              disabled={!file || tooBig || title.trim().length < 3 || upload.isPending}
              onClick={() => upload.mutate()}
            >
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
          {file && (
            <p className="text-xs text-muted-foreground">
              {(file.size / 1024 / 1024).toFixed(1)} MB
              {duration ? ` · ${clock(duration)}` : ''}
            </p>
          )}
        </div>
      )}

      {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {q.data?.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No tracks yet. Members see an empty Frequencies screen until you upload one.
        </p>
      )}
      <div className="divide-y rounded-md border">
        {(q.data ?? []).map((row) => {
          const meta = row.meta as {
            frequencyLabel?: string | null;
            durationSeconds?: number | null;
            sizeBytes?: number;
          };
          if (editing?.id === row.id) {
            return (
              <div key={row.id} className="space-y-2 p-3">
                <Input
                  className="h-8"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                />
                <div className="flex flex-wrap items-end gap-3">
                  <CategorySelect
                    value={editing.category}
                    onChange={(category) => setEditing({ ...editing, category })}
                  />
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Frequency label</label>
                    <Input
                      className="h-8 w-40"
                      value={editing.label}
                      onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                    />
                  </div>
                  <AudienceSelect
                    value={editing.premiumLevel}
                    onChange={(premiumLevel) => setEditing({ ...editing, premiumLevel })}
                  />
                  <Button
                    size="sm"
                    disabled={editing.title.trim().length < 3 || saveEdit.isPending}
                    onClick={() => saveEdit.mutate()}
                  >
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            );
          }
          return (
            <div
              key={row.id}
              className="flex flex-wrap items-start justify-between gap-3 p-3 text-sm"
            >
              <div className="min-w-0 space-y-1">
                <p className={row.active ? '' : 'text-muted-foreground line-through'}>
                  {row.title}
                </p>
                <p className="text-xs text-muted-foreground">
                  {COACHING_FREQUENCY_CATEGORY_LABELS[asCategory(row.category)]}
                  {meta.frequencyLabel ? ` · ${meta.frequencyLabel}` : ''}
                  {meta.durationSeconds ? ` · ${clock(meta.durationSeconds)}` : ''}
                  {meta.sizeBytes
                    ? ` · ${(meta.sizeBytes / 1024 / 1024).toFixed(1)} MB`
                    : ''} · {audienceLabel(row.premiumLevel)}
                </p>
                {row.audioUrl && (
                  <audio
                    controls
                    preload="none"
                    src={row.audioUrl}
                    className="h-8 w-full max-w-xs"
                  />
                )}
              </div>
              {canWrite && (
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setEditing({
                        id: row.id,
                        title: row.title ?? '',
                        category: asCategory(row.category),
                        label: meta.frequencyLabel ?? '',
                        premiumLevel: row.premiumLevel,
                      })
                    }
                  >
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggle.mutate(row)}>
                    {row.active ? 'Hide' : 'Show'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setRemoving(row)}>
                    Remove
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title="Remove this track?"
        description="Members stop seeing it at once. The file itself is deleted after the storage grace period."
        confirmLabel="Remove"
        confirmVariant="destructive"
        loading={remove.isPending}
        onConfirm={async () => {
          await remove.mutateAsync(removing!.id);
          setRemoving(null);
        }}
      />
    </div>
  );
}
